import React from "react";
import { CELL_SIZE, GRID_PADDING } from "../constants";
import { getSectionShape, OFFICE_THEMES } from "../utils";
import { SECTION_OPTIONS } from "../constants";

const OfficeGrid = ({
  office,
  sections,
  loading,
  selectedSectionId,
  dragState,
  previewPosition,
  handleSectionClick,
  handleSectionMouseDown,
  children,
}) => {
  const getRenderedPosition = (section) => {
    if (previewPosition && previewPosition.sectionId === section.id) {
      return previewPosition;
    }
    return { x: section.x, y: section.y };
  };

  // Generate background placeholders
  const placeholders = [];
  for (let r = 1; r <= office.gridRows; r++) {
    for (let c = 1; c <= office.gridColumns; c++) {
      placeholders.push({ r, c });
    }
  }

  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">
            {office.name || "Office Layout"}
          </h2>
          <p className="text-sm text-slate-400">
            Drag sections to reposition · Click a section to manage seats
          </p>
        </div>
        <div className="rounded-xl bg-slate-100 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-500">
          {loading ? "Saving..." : "Layout"}
        </div>
      </div>

      {children}

      <div className="overflow-auto max-h-[600px] rounded-[24px] border border-slate-100 bg-slate-50">
        <div
          className="relative"
          style={{
            height: office.gridRows * CELL_SIZE + GRID_PADDING * 2,
            minWidth: office.gridColumns * CELL_SIZE + GRID_PADDING * 2,
            backgroundImage:
              "linear-gradient(to right, rgba(148,163,184,0.15) 1px, transparent 1px), linear-gradient(to bottom, rgba(148,163,184,0.15) 1px, transparent 1px)",
            backgroundSize: `${CELL_SIZE}px ${CELL_SIZE}px`,
            backgroundPosition: `${GRID_PADDING}px ${GRID_PADDING}px`,
          }}
        >
          {sections.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500">
              No sections yet. Add your first section from the left panel.
            </div>
          )}
          {sections.map((section) => {
            const theme = getSectionShape(section.type);
            const position = getRenderedPosition(section);
            const isSelected = selectedSectionId === section.id;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => handleSectionClick(section)}
                onMouseDown={(event) => handleSectionMouseDown(event, section)}
                className={`absolute rounded-2xl border p-3 text-center shadow-sm transition ${theme.className}`}
                style={{
                  left: GRID_PADDING + (position.x - 1) * CELL_SIZE,
                  top: GRID_PADDING + (position.y - 1) * CELL_SIZE,
                  width: section.width * CELL_SIZE,
                  height: section.height * CELL_SIZE,
                  cursor:
                    dragState?.sectionId === section.id ? "grabbing" : "grab",
                  ...(isSelected && {
                    outline: `2.5px solid ${theme.selectedColor}`,
                    outlineOffset: "-2px",
                  }),
                }}
              >
                <div className="flex h-full flex-col items-center justify-center gap-1">
                  <p className="text-xs font-bold text-slate-800">
                    {section.name}
                  </p>
                  <div className="rounded bg-[#0000001a] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-700">
                    {section.type}
                  </div>
                  <p className="text-[10px] text-slate-500 font-medium">
                    {section.seats.length} seats
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        {[
          { className: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Rectangle (Bay)" },
          { className: "bg-green-50 text-green-800 border-green-200", label: "Cubicle" },
          { className: "bg-orange-50 text-orange-800 border-orange-200", label: "Cabin" },
        ].map((item) => (
          <span key={item.label} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${item.className}`}>
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default OfficeGrid;
